import { c as _c } from "react/compiler-runtime";
import { Avatar, AvatarFallback, AvatarImage } from "@repo/blacksmith-ui/avatar";
import { useActiveGithubOrgForUserNonSuspense, useConnectedGithubOrgsNonSuspense, useIsImpersonatingNonSuspense, type SsoBlockedOrg } from "@/hooks/useConnectedGithubOrgs";
import { githubReconnectLogin, markSsoReauthPending } from "@/lib/github-reauth";
import { orgTargetPath } from "@/lib/orgTargetPath";
import { useSsoReauthPrompt } from "@/hooks/useSsoReauthPrompt";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { safeLocalStorage } from "@/lib/storage";
import { cn } from "@repo/blacksmith-ui/cn";
import { ChevronRight, ShieldAlert, Sparkles, Check, Plus, RefreshCw } from "lucide-react";
import { useTheme } from "next-themes";
import Link from "next/link";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { BlackGitHubIcon, GitHubIcon, WhiteGitHubIcon } from "./GitHubIcon";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@repo/blacksmith-ui/dropdown-menu";
const EMPTY_ORGS: string[] = [];
export interface OrgSelectorOrg {
  login: string;
  avatarUrl: string;
  isNew?: boolean;
  isSuspended?: boolean;
  /** Route to navigate to when this org is selected. When set, the row
   * renders as a real link so modified clicks open in a new tab. */
  href?: string;
}
export interface GitHubOrgSelectorViewProps {
  orgs: OrgSelectorOrg[];
  activeOrgLogin?: string | null;
  /**
   * Orgs GitHub hid from the user's account because their OAuth token lacks
   * an active SAML SSO authorization. Surfaced as an amber notification dot
   * on the trigger and highlighted rows in the dropdown that deep-link to
   * GitHub's SSO authorization flow.
   */
  ssoBlockedOrgs?: SsoBlockedOrg[];
  isImpersonating?: boolean;
  /** Selection callback for org rows without an `href`; rows with an `href`
   * navigate via the link itself. */
  onSelectOrg?: (login: string) => void;
  onAddOrg: () => void;
  /** GitHub App installation URL; when set the "Add an organization" menu
   * item renders as a real link so modified clicks open in a new tab. */
  addOrgHref?: string;
  onAuthorizeOrg?: (org: SsoBlockedOrg) => void;
  /**
   * Blocked orgs the user has already clicked Authorize for. Their row swaps
   * Authorize for Reconnect (the silent OAuth re-login that actually restores
   * access); once the re-login lands the flag is gone, so an org that is still
   * blocked falls back to Authorize.
   */
  ssoReauthPendingOrgs?: string[];
  onReconnect?: () => void;
  /**
   * Whether the enclosing app-switcher rail is expanded. When collapsed the
   * trigger's border fades out so the org avatar reads as a bare icon in the
   * slim rail; it fades back in as the rail wipes open. Defaults to `true` so
   * standalone/story usages keep the bordered pill.
   */
  expanded?: boolean;
  /**
   * True while org data is still being fetched. `orgs` is empty during this
   * window too, so without this flag the empty-state ("Add an organization",
   * which renders a GitHub glyph) flashes on every refresh before the real
   * org avatar arrives. When loading, render the bordered pill with the
   * active org login when one is known (org routes), or a skeleton pill when
   * there is nothing to show yet (org-less routes).
   */
  isLoading?: boolean;
}
export function GitHubOrgSelectorView(t0) {
  const $ = _c(73);
  const {
    orgs,
    activeOrgLogin,
    ssoBlockedOrgs: t1,
    isImpersonating: t2,
    onSelectOrg,
    onAddOrg,
    addOrgHref,
    onAuthorizeOrg,
    ssoReauthPendingOrgs: t3,
    onReconnect,
    expanded: t4,
    isLoading: t5
  } = t0;
  let t6;
  if ($[0] !== t1) {
    t6 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const ssoBlockedOrgs = t6;
  const isImpersonating = t2 === undefined ? false : t2;
  const ssoReauthPendingOrgs = t3 === undefined ? EMPTY_ORGS : t3;
  const expanded = t4 === undefined ? true : t4;
  const isLoading = t5 === undefined ? false : t5;
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const {
    resolvedTheme: theme
  } = useTheme();
  let t7;
  let t8;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const handleOpenOrgSwitcher = () => {
        setIsOpen(true);
      };
      window.addEventListener("openOrgSwitcher", handleOpenOrgSwitcher);
      return () => {
        window.removeEventListener("openOrgSwitcher", handleOpenOrgSwitcher);
      };
    };
    t8 = [];
    $[2] = t7;
    $[3] = t8;
  } else {
    t7 = $[2];
    t8 = $[3];
  }
  useEffect(t7, t8);
  let t9;
  if ($[4] !== activeOrgLogin || $[5] !== orgs) {
    t9 = orgs.find(org => org.login === activeOrgLogin) ?? orgs.at(0);
    $[4] = activeOrgLogin;
    $[5] = orgs;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const activeOrg = t9;
  let t10;
  if ($[7] !== orgs) {
    t10 = orgs.some(_temp);
    $[7] = orgs;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  const hasNewInstallations = t10;
  const hasSsoBlockedOrgs = ssoBlockedOrgs.length > 0;
  if (!orgs.length && isLoading && !activeOrgLogin && !isImpersonating) {
    let t11;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <Skeleton className="h-8 w-full rounded-lg" />;
      $[9] = t11;
    } else {
      t11 = $[9];
    }
    return t11;
  }
  if (!orgs.length && !isLoading) {
    let t11;
    if ($[10] !== theme) {
      t11 = <div className="shrink-0">{theme === "dark" ? <WhiteGitHubIcon /> : <BlackGitHubIcon />}</div>;
      $[10] = theme;
      $[11] = t11;
    } else {
      t11 = $[11];
    }
    let t12;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <span className="justify-start w-40 truncate text-neutral-900 dark:text-neutral-100">Add an organization</span>;
      $[12] = t12;
    } else {
      t12 = $[12];
    }
    let t13;
    if ($[13] !== t11) {
      t13 = <DropdownMenuTrigger className="focus-visible:outline-hidden"><span className="flex items-center">{t11}{t12}</span></DropdownMenuTrigger>;
      $[13] = t11;
      $[14] = t13;
    } else {
      t13 = $[14];
    }
    let t14;
    if ($[15] !== isOpen || $[16] !== t13) {
      t14 = <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>{t13}</DropdownMenu>;
      $[15] = isOpen;
      $[16] = t13;
      $[17] = t14;
    } else {
      t14 = $[17];
    }
    let t15;
    if ($[18] !== onAddOrg || $[19] !== t14) {
      t15 = <div className="rounded-xl border-[1px] border-[var(--color-border)] px-3 py-2 text-neutral-500 text-sm font-medium w-50" onClick={onAddOrg}>{t14}</div>;
      $[18] = onAddOrg;
      $[19] = t14;
      $[20] = t15;
    } else {
      t15 = $[20];
    }
    return t15;
  }
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = open => {
      setIsOpen(open);
      if (!open) {
        setTimeout(() => {
          triggerRef.current?.blur();
        }, 0);
      }
    };
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const handleOpenChange = t11;
  const t12 = expanded ? "border-[#0c0c0d21] dark:border-white/16" : "border-transparent dark:border-transparent";
  let t13;
  if ($[22] !== t12) {
    t13 = cn("relative flex items-center w-full h-8 gap-2 px-1 overflow-hidden rounded-lg border bg-transparent focus-visible:outline-hidden cursor-pointer", "[transition:border-color_240ms_cubic-bezier(0.22,1,0.36,1)]", t12);
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== activeOrg?.avatarUrl || $[25] !== activeOrg?.login || $[26] !== activeOrgLogin || $[27] !== isImpersonating) {
    t14 = <Avatar className="size-[22px] shrink-0 border border-[#0c0c0d21] dark:border-white/24">{isImpersonating ? <ShieldAlert className="aspect-square h-full w-full text-orange-500" /> : <><AvatarImage src={activeOrg?.avatarUrl ?? ""} alt="active org logo" loading="eager" /><AvatarFallback className="uppercase">{(activeOrg?.login ?? activeOrgLogin)?.charAt(0)}</AvatarFallback></>}</Avatar>;
    $[24] = activeOrg?.avatarUrl;
    $[25] = activeOrg?.login;
    $[26] = activeOrgLogin;
    $[27] = isImpersonating;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const t15 = isImpersonating && "text-orange-500 font-bold";
  let t16;
  if ($[29] !== t15) {
    t16 = cn("flex-1 text-left truncate type-body-s-regular text-neutral-950 dark:text-white", t15);
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const t17 = (isImpersonating ? activeOrgLogin : activeOrg?.login ?? activeOrgLogin) ?? "";
  let t18;
  if ($[31] !== t16 || $[32] !== t17) {
    t18 = <span className={t16}>{t17}</span>;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <ChevronRight className="size-[18px] shrink-0 rotate-90 text-neutral-500 dark:text-neutral-400" />;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t13 || $[36] !== t14 || $[37] !== t18) {
    t20 = <DropdownMenuTrigger ref={triggerRef} className={t13}>{t14}{t18}{t19}</DropdownMenuTrigger>;
    $[35] = t13;
    $[36] = t14;
    $[37] = t18;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => {
      triggerRef.current?.blur();
    };
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== addOrgHref || $[41] !== onAddOrg) {
    t22 = addOrgHref ? <DropdownMenuItem key="add-new-org" asChild={true} icon={<Plus />}><a href={addOrgHref} onClick={() => setIsOpen(false)}>Add an organization</a></DropdownMenuItem> : <DropdownMenuItem key="add-new-org" icon={<Plus />} onClick={() => {
      setIsOpen(false);
      onAddOrg();
    }}>Add an organization</DropdownMenuItem>;
    $[40] = addOrgHref;
    $[41] = onAddOrg;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <DropdownMenuSeparator />;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== activeOrgLogin || $[45] !== onSelectOrg || $[46] !== orgs) {
    let t25;
    if ($[48] !== activeOrgLogin || $[49] !== onSelectOrg) {
      t25 = org_1 => {
        const isActive = org_1.login === activeOrgLogin;
        const rowOptions = {
          icon: <Avatar className="size-[18px] bg-neutral-100 dark:bg-neutral-700"><AvatarImage src={org_1.avatarUrl} alt={`${org_1.login} logo`} loading="eager" /><AvatarFallback className="uppercase text-[10px] text-neutral-900 dark:text-white">{org_1.login?.charAt(0)}</AvatarFallback></Avatar>,
          trailing: isActive || org_1.isNew || org_1.isSuspended ? <span className="flex items-center gap-2">{org_1.isNew && <span className="flex items-center gap-1 type-body-xs-regular text-green-600 dark:text-green-400"><Sparkles className="size-3" />New</span>}{org_1.isSuspended && <span className="type-body-xs-regular text-red-500">Suspended</span>}{isActive && <Check className="size-3.5 text-neutral-900 dark:text-white" strokeWidth={2.25} />}</span> : undefined
        };
        const rowClassName = cn(isActive && "font-medium");
        return org_1.href ? <DropdownMenuItem key={org_1.login} asChild={true} className={rowClassName} {...rowOptions}><Link href={org_1.href} onClick={() => setIsOpen(false)}>{org_1.login}</Link></DropdownMenuItem> : <DropdownMenuItem key={org_1.login} className={rowClassName} {...rowOptions} onClick={() => {
          setIsOpen(false);
          onSelectOrg?.(org_1.login);
        }}>{org_1.login}</DropdownMenuItem>;
      };
      $[48] = activeOrgLogin;
      $[49] = onSelectOrg;
      $[50] = t25;
    } else {
      t25 = $[50];
    }
    t24 = orgs.map(t25);
    $[44] = activeOrgLogin;
    $[45] = onSelectOrg;
    $[46] = orgs;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[51] !== hasSsoBlockedOrgs || $[52] !== onAuthorizeOrg || $[53] !== onReconnect || $[54] !== ssoBlockedOrgs || $[55] !== ssoReauthPendingOrgs) {
    t25 = hasSsoBlockedOrgs && <><DropdownMenuSeparator /><div className="flex items-center gap-1.5 px-2 pt-2 pb-1 type-body-xxs-regular text-amber-600 dark:text-amber-400"><ShieldAlert className="w-3 h-3 shrink-0" />Hidden until you re-authorize SSO</div>{ssoBlockedOrgs.map(org_2 => {
        const reconnectPending = ssoReauthPendingOrgs.includes(org_2.name);
        return <DropdownMenuItem key={org_2.name} className="bg-amber-500/10 focus:bg-amber-500/20 data-[highlighted]:bg-amber-500/20 dark:bg-amber-400/10 dark:focus:bg-amber-400/20 dark:data-[highlighted]:bg-amber-400/20" icon={<span className="flex size-[18px] items-center justify-center rounded-full bg-amber-500/15 ring-1 ring-amber-500/50"><span className="uppercase text-[10px] text-amber-600 dark:text-amber-400">{org_2.name.charAt(0)}</span></span>} trailing={<span className="flex items-center gap-1 type-body-xs-regular text-amber-600 dark:text-amber-400">{reconnectPending ? <>Reconnect<RefreshCw className="size-3" /></> : <>Authorize<GitHubIcon className="size-3" /></>}</span>} onClick={() => {
          if (reconnectPending) {
            onReconnect?.();
            return;
          }
          window.open(org_2.sso_url, "_blank", "noopener,noreferrer");
          onAuthorizeOrg?.(org_2);
        }}>{org_2.name}</DropdownMenuItem>;
      })}</>;
    $[51] = hasSsoBlockedOrgs;
    $[52] = onAuthorizeOrg;
    $[53] = onReconnect;
    $[54] = ssoBlockedOrgs;
    $[55] = ssoReauthPendingOrgs;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <DropdownMenuSeparator />;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <div className="px-2 py-2 type-body-xxs-regular text-neutral-950 dark:text-white text-center">Missing an org?{" "}<a href="https://docs.blacksmith.sh/introduction/quickstart#dont-see-your-organization-in-the-blacksmith-dashboard" target="_blank" rel="noopener noreferrer" className="text-neutral-900 dark:text-neutral-100 underline underline-offset-2 hover:text-neutral-700 dark:hover:text-neutral-300">Here's why!</a></div>;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t22 || $[60] !== t24 || $[61] !== t25) {
    t28 = <DropdownMenuContent align="start" side="bottom" sideOffset={0} alignOffset={0} className="w-[239px]" onInteractOutside={t21}>{t22}{t23}{t24}{t25}{t26}{t27}</DropdownMenuContent>;
    $[59] = t22;
    $[60] = t24;
    $[61] = t25;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== isOpen || $[64] !== t20 || $[65] !== t28) {
    t29 = <DropdownMenu open={isOpen} onOpenChange={handleOpenChange} modal={false}>{t20}{t28}</DropdownMenu>;
    $[63] = isOpen;
    $[64] = t20;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== hasNewInstallations || $[68] !== hasSsoBlockedOrgs) {
    t30 = hasSsoBlockedOrgs ? <div className="pointer-events-none absolute -top-1 -right-1 flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" /><span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500" /></div> : hasNewInstallations ? <div className="pointer-events-none absolute -top-1 -right-1 flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" /><span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" /></div> : null;
    $[67] = hasNewInstallations;
    $[68] = hasSsoBlockedOrgs;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] !== t29 || $[71] !== t30) {
    t31 = <div className="relative w-full text-sm font-medium">{t29}{t30}</div>;
    $[70] = t29;
    $[71] = t30;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  return t31;
}
function _temp(org_0) {
  return org_0.isNew;
}
export function GitHubOrgSelector(t0) {
  const $ = _c(26);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    expanded: t2
  } = t1;
  const expanded = t2 === undefined ? true : t2;
  const router = useRouter();
  const {
    connectedGithubOrgs,
    isLoading
  } = useConnectedGithubOrgsNonSuspense();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUserNonSuspense();
  const isImpersonating = useIsImpersonatingNonSuspense();
  let t3;
  if ($[2] !== connectedGithubOrgs?.sso_blocked_orgs) {
    t3 = connectedGithubOrgs?.sso_blocked_orgs ?? [];
    $[2] = connectedGithubOrgs?.sso_blocked_orgs;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const ssoBlockedOrgs = t3;
  const ssoReauthPendingOrgs = useSsoReauthPrompt(connectedGithubOrgs ? ssoBlockedOrgs : undefined);
  const getVisitedOrgs = _temp2;
  let t4;
  let t5;
  if ($[4] !== activeGithubOrg) {
    t4 = () => {
      if (activeGithubOrg) {
        const visited = getVisitedOrgs();
        visited.add(activeGithubOrg);
        safeLocalStorage.set("blacksmith_visited_orgs", JSON.stringify(Array.from(visited)));
      }
    };
    t5 = [activeGithubOrg];
    $[4] = activeGithubOrg;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = org => {
      const createdDate = new Date(org.created_at);
      const now = new Date();
      const fifteenMinutesAgo = new Date(now.getTime() - 900000);
      const isRecent = createdDate > fifteenMinutesAgo;
      const isVisited = getVisitedOrgs().has(org.account.login);
      return isRecent && !isVisited;
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const isNewInstallation = t6;
  const routePattern = router.pathname;
  let t7;
  if ($[8] !== connectedGithubOrgs?.installations) {
    t7 = connectedGithubOrgs?.installations ?? [];
    $[8] = connectedGithubOrgs?.installations;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const installations = t7;
  let t8;
  if ($[10] !== activeGithubOrg || $[11] !== installations || $[12] !== routePattern) {
    let t9;
    if ($[14] !== activeGithubOrg || $[15] !== installations.length || $[16] !== routePattern) {
      t9 = org_0 => ({
        login: org_0.account.login,
        avatarUrl: org_0.account.avatar_url,
        href: orgTargetPath(routePattern, org_0.account.login),
        isNew: installations.length > 1 && org_0.account.login !== activeGithubOrg && org_0.account.type !== "User" && isNewInstallation(org_0),
        isSuspended: Boolean(org_0.suspended_by)
      });
      $[14] = activeGithubOrg;
      $[15] = installations.length;
      $[16] = routePattern;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    t8 = installations.map(t9);
    $[10] = activeGithubOrg;
    $[11] = installations;
    $[12] = routePattern;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const orgs = t8;
  let t9;
  if ($[18] !== activeGithubOrg || $[19] !== expanded || $[20] !== isImpersonating || $[21] !== isLoading || $[22] !== orgs || $[23] !== ssoBlockedOrgs || $[24] !== ssoReauthPendingOrgs) {
    t9 = <GitHubOrgSelectorView orgs={orgs} activeOrgLogin={activeGithubOrg} ssoBlockedOrgs={ssoBlockedOrgs} isImpersonating={isImpersonating} isLoading={isLoading} expanded={expanded} addOrgHref={process.env.NEXT_PUBLIC_GITHUB_APP_INSTALLATION_URL} onAddOrg={_temp3} onAuthorizeOrg={_temp4} ssoReauthPendingOrgs={ssoReauthPendingOrgs} onReconnect={githubReconnectLogin} />;
    $[18] = activeGithubOrg;
    $[19] = expanded;
    $[20] = isImpersonating;
    $[21] = isLoading;
    $[22] = orgs;
    $[23] = ssoBlockedOrgs;
    $[24] = ssoReauthPendingOrgs;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}
function _temp4(org_1) {
  return markSsoReauthPending(org_1.name);
}
function _temp3() {
  window.location.href = `${process.env.NEXT_PUBLIC_GITHUB_APP_INSTALLATION_URL}`;
}
function _temp2() {
  const stored = safeLocalStorage.get("blacksmith_visited_orgs");
  if (!stored) {
    return new Set();
  }
  try {
    return new Set(JSON.parse(stored));
  } catch {
    return new Set();
  }
}